import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import DataTable from '../../../components/ui/DataTable';
import OptionsMenu from '../../../components/ui/OptionsMenu';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import LoadErrorBanner from '../../../components/ui/LoadErrorBanner';
import DocumentViewerModal from '../../../components/ui/DocumentViewerModal';
import GenericFormDrawer from '../../../components/GenericFormDrawer';
import { formatDate } from '../../../utils/Utils';
import { DOCUMENT_ACCEPT } from '../../../utils/documentAccept';
import {
  getDocuments, createDocument, deleteDocument, CANDIDATE_DOCUMENT_TYPES, DOCUMENT_NAME_MAX,
} from '../config/profileApi';

// BELGELER — adayın sertifika/diploma/portföy/referans mektubu vb. dosyaları.
//
// Yükleme iki adımdır ve İKİSİ DE ŞARTTIR:
//   1. `POST /v1/storage/presign?purpose=candidateDocument` → R2'ye PUT. Bu adımı
//      GenericFormDrawer'ın `pictureUpload` alanı kendi içinde yürütür
//      (uploadSchemaFiles) — `onSave`e ulaşan `fileUrl` bu kullanıcıya İHRAÇ EDİLMİŞ
//      gerçek adrestir. `candidateDocument` amacı ÖZEL kümeyi seçer.
//   2. `POST v1/ats-candidates/{id}/documents` kaydı. Sunucu adresi
//      IStorageAddressValidator ile doğrular — elle yazılmış adres REDDEDİLİR.
// Görüntüleme `DocumentViewerModal` (imzalı `/v1/storage/view`) — ham adres asla
// <a href>/<iframe>'e verilmez; erişim kararını FileAccessGuard verir.
// Yükleme/silme AT.Write; görüntüleme aday görünürlüğü olan herkese.

export const DOCUMENT_UPLOAD_PURPOSE = 'candidateDocument';

// Seçilen dosyanın üst verisi form durumuna türetilir (`deriveFields`) — yükleme
// adımı File'ı adrese çevirdikten sonra tür/boyut bilgisi kaybolurdu.
export const deriveFileMeta = (file) => {
  if (!file || typeof file !== 'object' || !('name' in file)) return {};
  return {
    contentType: file.type || null,
    sizeBytes: Number.isFinite(file.size) ? file.size : null,
    originalFileName: file.name || null,
  };
};

export const buildDocumentPayload = (values) => {
  const name = ((values.name || '').trim() || values.originalFileName || '').slice(0, DOCUMENT_NAME_MAX);
  return {
    name,
    documentType: values.documentType || 'other',
    fileUrl: values.fileUrl,
    contentType: values.contentType || null,
    sizeBytes: values.sizeBytes ?? null,
  };
};

const formatSize = (bytes) => {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function CandidateDocumentsTab({ candidateId, writable }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [fetching, setFetching] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [docs, setDocs] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, doc: null });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    setFetching(true);
    getDocuments(candidateId)
      .then(({ ok, items }) => {
        if (!alive) return;
        setLoadFailed(!ok);
        setDocs(items);
      })
      .finally(() => { if (alive) setFetching(false); });
    return () => { alive = false; };
  }, [candidateId, reloadKey]);

  const typeLabel = useCallback(
    (v) => t(`ats.candidateProfile.documentType.${v}`, { defaultValue: v }),
    [t],
  );

  const typeOptions = useMemo(
    () => CANDIDATE_DOCUMENT_TYPES.map((id) => ({ id, name: typeLabel(id) })),
    [typeLabel],
  );

  const schema = useMemo(() => [
    {
      name: 'fileUrl',
      label: t('ats.candidateProfile.documentFile'),
      type: 'pictureUpload',
      grid: 12,
      required: true,
      purpose: DOCUMENT_UPLOAD_PURPOSE,
      accept: DOCUMENT_ACCEPT,
      maxSize: 10,
      deriveFields: deriveFileMeta,
    },
    {
      name: 'name',
      label: t('ats.candidateProfile.documentName'),
      type: 'text',
      grid: 12,
      description: t('ats.candidateProfile.documentNameHint'),
    },
    {
      name: 'documentType',
      label: t('ats.candidateProfile.documentTypeLabel'),
      type: 'select',
      grid: 12,
      required: true,
      options: typeOptions,
      defaultValue: 'other',
    },
  ], [t, typeOptions]);

  const handleSave = useCallback(async (values) => {
    // Yükleme başarısızsa alan hâlâ File'dır (uploadSchemaFiles hatayı yutar) —
    // adres olmadan kayıt ATILMAZ, çekmece açık kalır.
    if (typeof values.fileUrl !== 'string' || !values.fileUrl) return false;
    const res = await createDocument(candidateId, buildDocumentPayload(values));
    if (!res.ok) return false;
    setDrawerOpen(false);
    setReloadKey((k) => k + 1);
    return true;
  }, [candidateId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.doc) return;
    setDeleting(true);
    const res = await deleteDocument(candidateId, deleteDialog.doc.id);
    setDeleting(false);
    setDeleteDialog({ isOpen: false, doc: null });
    if (res.ok) setReloadKey((k) => k + 1);
  }, [candidateId, deleteDialog.doc]);

  const buildActions = useCallback((doc) => {
    const actions = [{ type: 'details', label: t('ats.candidateProfile.viewDocument'), onClick: () => setViewing(doc) }];
    if (writable) {
      actions.push({ type: 'divider' });
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, doc }),
      });
    }
    return actions;
  }, [t, writable]);

  const columns = useMemo(() => [
    { name: 'name', label: t('ats.candidateProfile.documentName'), options: { filter: false, sort: false } },
    {
      name: 'documentType',
      label: t('ats.candidateProfile.documentTypeLabel'),
      options: { filter: false, sort: false, customBodyRender: (v) => (v ? typeLabel(v) : '-') },
    },
    {
      name: 'sizeBytes',
      label: t('ats.candidateProfile.documentSize'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatSize(v) },
    },
    {
      name: 'uploadedByName',
      label: t('ats.candidateProfile.uploadedBy'),
      options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
    },
    {
      name: 'createDate',
      label: t('ats.candidateProfile.uploadedAt'),
      options: { filter: false, sort: false, customBodyRender: (v) => formatDate(v, lang) },
    },
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const doc = docs[tableMeta.rowIndex];
          if (!doc) return null;
          return (
            <span onClick={(e) => e.stopPropagation()} role="presentation">
              <OptionsMenu actions={buildActions(doc)} row={doc} />
            </span>
          );
        },
      },
    },
  ], [t, lang, docs, typeLabel, buildActions]);

  return (
    <div data-role="candidate-documents">
      {loadFailed && <LoadErrorBanner message={t('ats.candidateProfile.documentsLoadFailed')} />}

      {writable && (
        <GenericFormDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          title={t('ats.candidateProfile.newDocument')}
          schema={schema}
          initialValues={null}
          onSave={handleSave}
        />
      )}

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.candidateProfile.deleteDocumentConfirm', { name: deleteDialog.doc?.name || '' })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, doc: null })}
        loading={deleting}
      />

      <DocumentViewerModal
        open={!!viewing}
        fileUrl={viewing?.fileUrl}
        title={viewing?.name}
        onClose={() => setViewing(null)}
      />

      <DataTable
        title={t('ats.candidateProfile.documentsTitle')}
        fetching={fetching}
        columns={columns}
        data={docs}
        totalRowCount={docs.length}
        serverSide={false}
        disableFilters
        canCreateNewRecord={!!writable}
        newButtonLabel={t('ats.candidateProfile.newDocument')}
        onNewButtonClick={() => setDrawerOpen(true)}
        onRowClick={(doc) => setViewing(doc)}
      />
    </div>
  );
}
